import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, GraduationCap, BookOpen, Clock, Users, CheckCircle, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { diplomatePrograms } from '../../data/programs';
import SEO from '../../components/SEO';
import CallbackWidget from '../../components/CallbackWidget';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

export default function Diplomante() {
  return (
    <div data-testid="diplomante-page">
      <SEO
        title="Formation Diplomante Abidjan - Bachelor & MBA"
        description="Centre de Formation Diplomante CEFAT : Bachelor en Business, MBA, Master. Diplomes reconnus, stages en entreprise. Inscriptions ouvertes a Abidjan, Cote d'Ivoire."
        keywords="formation diplomante Abidjan, Bachelor Business Abidjan, MBA Cote d'Ivoire, Master management Abidjan, diplome ecole commerce Afrique, CEFAT diplomante"
        url="/diplomante"
      />
      {/* Hero */}
      <section className="relative bg-[#003366] py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-400/10 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <span className="inline-block bg-[#FFD700] text-[#003366] px-4 py-2 rounded-full text-sm font-bold mb-6">
                Centre de Formation Diplômante
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
                Bachelor & Master <span className="text-[#FFD700]">d'Excellence</span>
              </h1>
              <p className="text-xl text-white/80 mb-8 leading-relaxed">
                Des formations diplômantes reconnues internationalement, conçues pour former 
                les leaders et managers de demain en Business et Technologies.
              </p>
              <Link to="/preinscription?center=diplomante">
                <Button className="bg-[#DC143C] hover:bg-[#B01030] text-white rounded-full px-8 py-6 text-lg font-bold shadow-lg">
                  Je me préinscris <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="hidden lg:block"
            >
              <img 
                src="https://images.unsplash.com/photo-1645263012668-a6617115f9b9?w=600&h=500&fit=crop"
                alt="Étudiants diplômants"
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Bachelor Programs */}
      <section className="py-20 bg-white" data-testid="bachelor-section">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div {...fadeInUp} className="mb-12">
            <span className="badge badge-blue mb-4">Cycle Bachelor</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#003366]">
              Programmes Bachelor (Bac+3)
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {diplomatePrograms.bachelor.map((program, index) => (
              <motion.div
                key={program.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#F8FAFC] rounded-2xl p-8 card-hover"
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 bg-[#003366] rounded-xl flex items-center justify-center flex-shrink-0">
                    <GraduationCap className="text-white" size={28} />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-[#003366]">{program.name}</h3>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      <span className="flex items-center gap-1"><Clock size={14} /> {program.duration}</span>
                      <span className="flex items-center gap-1"><BookOpen size={14} /> Niveau {program.level}</span>
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 mb-6">{program.description}</p>
                
                <div className="mb-6">
                  <h4 className="font-semibold text-[#003366] mb-3">Modules principaux</h4>
                  <div className="flex flex-wrap gap-2">
                    {program.modules.slice(0, 4).map((module, i) => (
                      <span key={i} className="bg-white px-3 py-1 rounded-full text-sm text-gray-600 border">
                        {module}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="font-semibold text-[#003366] mb-3">Débouchés</h4>
                  <ul className="space-y-2">
                    {program.career.slice(0, 3).map((career, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-600">
                        <CheckCircle size={16} className="text-green-600" /> {career}
                      </li>
                    ))}
                  </ul>
                </div>

                <Link to={`/preinscription?center=diplomante&program=${program.id}`}>
                  <Button className="w-full bg-[#003366] hover:bg-[#002244] text-white rounded-full">
                    Je postule <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Master Programs */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="master-section">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div {...fadeInUp} className="mb-12">
            <span className="badge badge-red mb-4">Cycle Master</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#003366]">
              Programmes MBA & Master (Bac+5)
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {diplomatePrograms.master.map((program, index) => (
              <motion.div
                key={program.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-md card-hover"
              >
                <div className="w-12 h-12 bg-[#DC143C] rounded-lg flex items-center justify-center mb-4">
                  <GraduationCap className="text-white" size={24} />
                </div>
                <h3 className="font-heading text-lg font-bold text-[#003366] mb-2">{program.name}</h3>
                <div className="flex items-center gap-3 text-sm text-gray-500 mb-4">
                  <span>{program.duration}</span>
                  <span>•</span>
                  <span>Niveau {program.level}</span>
                </div>
                <p className="text-gray-600 text-sm mb-4">{program.description}</p>
                
                <Link to={`/preinscription?center=diplomante&program=${program.id}`}>
                  <Button variant="outline" className="w-full border-[#003366] text-[#003366] hover:bg-[#003366] hover:text-white rounded-full text-sm">
                    En savoir plus <ArrowRight size={14} className="ml-2" />
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Admission Process */}
      <section className="py-20 bg-white" data-testid="admission-section">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="badge badge-gold mb-4">Admission</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#003366] mb-4">
              Processus d'Admission
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Notre processus de sélection rigoureux garantit la qualité de nos promotions et 
              l'excellence de notre communauté académique.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-5 gap-6">
            {diplomatePrograms.admission.steps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#003366] rounded-full flex items-center justify-center mx-auto mb-4 text-white font-heading text-2xl font-bold">
                    {step.step}
                  </div>
                  <h3 className="font-heading font-bold text-[#003366] mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-600">{step.description}</p>
                </div>
                {index < 4 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gray-200" />
                )}
              </motion.div>
            ))}
          </div>

          {/* Documents requis */}
          <motion.div {...fadeInUp} className="mt-16 bg-[#F8FAFC] rounded-2xl p-8">
            <h3 className="font-heading text-xl font-bold text-[#003366] mb-6 flex items-center gap-3">
              <FileText className="text-[#DC143C]" /> Documents requis
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {diplomatePrograms.admission.documents.map((doc, index) => (
                <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-lg">
                  <CheckCircle className="text-green-600 flex-shrink-0" size={18} />
                  <span className="text-gray-700">{doc}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Callback Widget */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <CallbackWidget variant="inline" center="diplomante" />
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#003366]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à rejoindre l'excellence ?
          </h2>
          <p className="text-white/80 mb-8 text-lg">
            Les inscriptions pour l'année académique 2024-2025 sont ouvertes.
          </p>
          <Link to="/preinscription?center=diplomante">
            <Button className="bg-[#DC143C] hover:bg-[#B01030] text-white rounded-full px-10 py-6 text-lg font-bold shadow-lg">
              Je me préinscris maintenant <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
